import React from "react";

import { config as springConfig, animated, useSpring } from "@react-spring/web";
import { some } from "lodash/fp";
import { useLocation } from "react-router-dom";

import styled from "@mui/material/styles/styled";

import useDailyRead from "@app/analytics/hooks/recordings/useDailyRead";
import useDailyReadContext from "@app/analytics/hooks/useDailyReadContext";
import useDailyReadStore from "@app/analytics/stores/useDailyReadStore";
import { DailyReadMetadata } from "@app/analytics/types";
import ROUTES from "@app/common/routes";

import Stack, { StackProps } from "@rui/Stack";

import DailyReadPlayer from "./DailyReadPlayer";

interface DailyReadPlayerDialogProps {
  metadata: DailyReadMetadata;
  minimized: boolean;
}

type ContainerProps = StackProps & { minimized: boolean };
const Container = styled(({ minimized: _m, ...props }: ContainerProps) => (
  <Stack {...props} />
))(({ minimized }) => ({
  width: minimized ? "fit-content" : "400px",
  maxWidth: "100%",
  boxShadow: minimized ? undefined : "0px 0px 16px 0px #0000001A",
  "&.hidden": {
    opacity: 0,
    zIndex: -100000,
  },
}));

const HIDDEN_ROUTES = [
  // the FYP has the daily read as an embedded module (but the rest of the FYx pages don't),
  // so we don't need to also show it as a dialog on the FYP specifically
  { path: ROUTES.FOR_YOU, exact: true },
  // don't show it in the checkout flow because it just gets in the way, even if minimized
  { path: ROUTES.BILLING_CHECKOUT_ROOT, exact: false },
];

const isOnHiddenRoute = (pathname: string) =>
  some(
    ({ path, exact }) => (exact ? pathname === path : pathname.includes(path)),
    HIDDEN_ROUTES
  );

const DailyReadPlayerDialog = ({
  metadata,
  minimized,
}: DailyReadPlayerDialogProps) => {
  const location = useLocation();

  const style = useSpring({
    from: { bottom: minimized ? -48 : -200, right: 24 },
    to: { bottom: minimized ? 8 : -1, right: 24 },
    config: minimized ? springConfig.wobbly : springConfig.stiff,
  });

  // render as hidden instead of rendering null when hidden, so that if the user is
  // navigating around while listening to it, it will still play seamlessly.
  const isHidden = isOnHiddenRoute(location.pathname);

  return (
    <animated.div
      style={{ position: "fixed", zIndex: isHidden ? -100000 : 1000, ...style }}
    >
      <Container
        id="daily-read-player-dialog"
        className={isHidden ? "hidden" : undefined}
        minimized={minimized}
      >
        <DailyReadPlayer
          dismissible
          forCurrentUser
          metadata={metadata}
          minimizable
          shareable
          withTranscript
          sx={{ borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }}
        />
      </Container>
    </animated.div>
  );
};

DailyReadPlayerDialog.displayName = "DailyReadPlayerDialog";

const DailyReadPlayerDialogGate = () => {
  const { metadata } = useDailyRead();
  const { lastMinimizedId } = useDailyReadContext();
  const minimized = useDailyReadStore(
    (state) => state.minimized || lastMinimizedId === metadata?.id
  );

  if (!metadata) return null;

  return <DailyReadPlayerDialog metadata={metadata} minimized={minimized} />;
};

export default DailyReadPlayerDialogGate;
